import React, { Component } from 'react';
import { arrayOf, bool, func, string } from 'prop-types';
import classNames from 'classnames';

import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { FormattedMessage } from '../../util/reactIntl';
import { ensureCurrentUser } from '../../util/data';
import { propTypes } from '../../util/types';
import { isUserAuthorized } from '../../util/userHelpers';
import { pathByRouteName } from '../../util/routes';
import {
  updateProfile,
  updateProfileInProgress,
} from '../../containers/ProfileSettingsPage/ProfileSettingsPage.duck';
import { hasUserType } from '../../util/genericHelpers';

import { Modal } from '../../components';

import EmailReminder from './EmailReminder';
import { useSelector } from 'react-redux';
import { authenticationInProgress, selectIsAuthenticated } from '../../ducks/auth.duck';
import UserTypeDetails from '../UserTypeDetails/UserTypeDetails';

import css from './ModalMissingInformation.module.css';

const MISSING_INFORMATION_MODAL_WHITELIST = [
  'LoginPage',
  'SignupPage',
  'ContactDetailsPage',
  'EmailVerificationPage',
  'PasswordResetPage',
  'StripePayoutPage',
];

const EMAIL_VERIFICATION = 'EMAIL_VERIFICATION';
const USER_TYPE_VERIFICATION = 'USER_TYPE_VERIFICATION';

class ModalMissingInformation extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showMissingInformationReminder: null,
      hasSeenMissingInformationReminder: false,
    };
    this.handleMissingInformationReminder = this.handleMissingInformationReminder.bind(this);
  }

  componentDidUpdate() {
    const {
      currentUser,
      currentUserHasListings,
      currentUserHasOrders,
      location,
      isAuthenticated,
      authInProgress,
    } = this.props;
    const user = ensureCurrentUser(currentUser);
    this.handleMissingInformationReminder(
      user,
      currentUserHasListings,
      currentUserHasOrders,
      location,
      isAuthenticated,
      authInProgress
    );
  }

  handleMissingInformationReminder(
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    newLocation,
    isAuthenticated,
    authInProgress
  ) {
    const routes = this.props.routeConfiguration;
    const whitelistedPaths = MISSING_INFORMATION_MODAL_WHITELIST.map(page =>
      pathByRouteName(page, routes)
    );

    // Is the current page whitelisted?
    const isPageWhitelisted = whitelistedPaths.includes(newLocation.pathname);

    const isUserTypeMissing = !hasUserType(currentUser);

    // Track if path changes inside Page level component
    const pathChanged = newLocation.pathname !== this.props.location.pathname;
    const notRemindedYet =
      !this.state.showMissingInformationReminder && !this.state.hasSeenMissingInformationReminder;

    // Is the reminder already shown on current page
    const showOnPathChange = notRemindedYet || pathChanged;

    if (
      isUserTypeMissing &&
      this.state.showMissingInformationReminder !== USER_TYPE_VERIFICATION &&
      isAuthenticated &&
      !authInProgress
    ) {
      this.setState({ showMissingInformationReminder: USER_TYPE_VERIFICATION });
    }

    if (!isPageWhitelisted && showOnPathChange) {
      const hasOrders = currentUserHasOrders === true;
      const hasListingsOrOrders = currentUserHasListings || hasOrders;

      const emailUnverified = !!currentUser.id && !currentUser.attributes.emailVerified;
      const emailVerificationNeeded = hasListingsOrOrders && emailUnverified;

      // If user type is missing, set the state immediately
      if (emailVerificationNeeded) {
        // Show email verification reminder if needed
        this.setState({ showMissingInformationReminder: EMAIL_VERIFICATION });
      }
    }
  }

  render() {
    const {
      rootClassName,
      className,
      dispatch,
      containerClassName,
      currentUser,
      sendVerificationEmailInProgress,
      sendVerificationEmailError,
      onManageDisableScrolling,
      onResendVerificationEmail,
      rootUserClassName,
      userFields,
      userTypes,
      updateInProgress,
    } = this.props;

    const user = ensureCurrentUser(currentUser);
    const classes = classNames(rootClassName || css.root, className);

    const handleUpdateProfile = values => {
      const updatedValues = Object.entries(values).reduce(
        (acc, [key, value]) => ({
          ...acc,
          [key.startsWith('pub_') ? key.slice(4) : key]: value,
        }),
        {}
      );

      const finalValues = {
        publicData: updatedValues,
      };

      dispatch(updateProfile(finalValues)).then(() => {
        this.setState({ showMissingInformationReminder: null });
      });
    };

    let content = null;

    const currentUserLoaded = user && user.id;

    if (currentUserLoaded && isUserAuthorized(currentUser)) {
      if (this.state.showMissingInformationReminder === USER_TYPE_VERIFICATION) {
        content = (
          <UserTypeDetails
            className={rootUserClassName}
            onSubmit={handleUpdateProfile}
            inProgress={updateInProgress}
            userFields={userFields}
            userTypes={userTypes}
          />
        );
      } else if (this.state.showMissingInformationReminder === EMAIL_VERIFICATION) {
        content = (
          <EmailReminder
            className={classes}
            user={user}
            onResendVerificationEmail={onResendVerificationEmail}
            sendVerificationEmailInProgress={sendVerificationEmailInProgress}
            sendVerificationEmailError={sendVerificationEmailError}
          />
        );
      }
    }

    const closeButtonMessage = (
      <FormattedMessage id="ModalMissingInformation.closeVerifyEmailReminder" />
    );

    return (
      <Modal
        id="MissingInformationReminder"
        containerClassName={containerClassName}
        isOpen={!!this.state.showMissingInformationReminder}
        onClose={() => {
          this.setState({
            showMissingInformationReminder: null,
            hasSeenMissingInformationReminder: true,
          });
        }}
        usePortal
        hideCloseButton
        onManageDisableScrolling={onManageDisableScrolling}
        closeButtonMessage={closeButtonMessage}
      >
        {content}
      </Modal>
    );
  }
}

ModalMissingInformation.defaultProps = {
  className: null,
  rootClassName: null,
  currentUser: null,
};

ModalMissingInformation.propTypes = {
  id: string.isRequired,
  className: string,
  rootClassName: string,
  containerClassName: string,

  currentUser: propTypes.currentUser,
  onManageDisableScrolling: func.isRequired,
  sendVerificationEmailError: propTypes.error,
  sendVerificationEmailInProgress: bool.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,
};

const EnhancedModalMissingInformation = props => {
  const routeConfiguration = useRouteConfiguration();

  const updateInProgress = useSelector(updateProfileInProgress);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const authInProgress = useSelector(authenticationInProgress);

  return (
    <ModalMissingInformation
      {...{
        routeConfiguration,
        updateInProgress,
        isAuthenticated,
        authInProgress,
        ...props,
      }}
    />
  );
};

export default EnhancedModalMissingInformation;
